import { Modal, Radio } from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import { buildPath } from '../../dux/modules/utils'
import AssetPreviewThumbnail from '../common/AssetPreviewThumbnail'
import TreeNode from '../sidebar/TreeNode'
import DisplayTextOverride from './DisplayTextOverride'
import '../styles/Modal.css'

class SelectAssetModal extends React.Component {
  state = {
    activeNode:
      this.props.modalProps.selectedAsset || this.props.folders.activeFolder,
    activeFolder:
      this.props.modalProps.selectedFolder || this.props.folders.activeFolder,
    openFolderKeys: this.props.modalProps.selectedFolder
      ? buildPath(this.props.modalProps.selectedFolder, this.props.folders.byId)
      : this.props.appState.openFolderKeys,
    activeAsset: this.props.modalProps.selectedAsset || '',
    textOverride: this.props.modalProps.selectedDisplayText || '',
    // Only meaningful when modalProps.allowFolderTarget is set: whether the
    // user has picked an asset or a folder out of the tree. Null until they
    // click something, so the folder the tree happens to open on is not
    // mistaken for a deliberate choice.
    targetType: this.props.modalProps.selectedTargetType || null,
    // Only meaningful when the modal has a split tree (see hasSplitTree):
    // which of the two trees is showing. Opens on the folder tree when
    // editing an existing folder target, otherwise on the asset tree.
    treeMode:
      this.props.modalProps.selectedTargetType === 'folder'
        ? 'folder'
        : 'asset',
  }

  // True when the caller accepts either an asset or a folder from the tree
  allowFolderTarget = () => !!this.props.modalProps.allowFolderTarget

  /*
   *  True when assets and folders are picked from different trees. Callers
   *  that accept folder targets may pass a separate folderTreeRoot (the
   *  published tree, which is what the apps can navigate to) while assets
   *  keep coming from treeRoot (the library, published or not). The modal
   *  then shows one tree at a time, switched by a toggle.
   */
  hasSplitTree = () =>
    this.allowFolderTarget() && !!this.props.modalProps.folderTreeRoot

  // True when the folder tree is the one on screen
  inFolderMode = () => this.hasSplitTree() && this.state.treeMode === 'folder'

  // Root folders of whichever tree is on screen
  currentTreeRoot = () =>
    this.inFolderMode()
      ? this.props.modalProps.folderTreeRoot
      : this.props.modalProps.treeRoot

  // True when the tree should list assets alongside folders
  showAssets = () => {
    if (this.inFolderMode()) return false
    return this.props.modalProps.showAssets === undefined
      ? true
      : this.props.modalProps.showAssets
  }

  // Switching trees drops the selection, since a node from one tree is not
  // a valid pick in the other
  setTreeMode = (treeMode) => {
    if (treeMode === this.state.treeMode) return
    this.setState({
      treeMode,
      activeNode: null,
      activeAsset: null,
      targetType: null,
    })
  }

  saveFormRef = (ref) => (this.form = ref)

  // Scroll Selected Asset into view
  saveContainerRef = (ref) => {
    if (ref) {
      if (this.props.modalProps.selectedAsset) {
        const list = document.getElementsByClassName('node-active')
        if (list.length > 0) {
          this.container = ref
          ref.scrollTop = list[0].offsetTop
        }
      }
    }
  }

  /*
   *  A selection is complete when an asset is picked, or - for callers that
   *  take either - when a folder has been clicked. Modals opened with
   *  showAssets === false have always defaulted to the active folder, so they
   *  can save immediately.
   */
  canSave = () => {
    if (this.allowFolderTarget() && this.state.targetType === 'folder')
      return !!this.state.activeFolder
    return (
      !!this.state.activeAsset || this.props.modalProps.showAssets === false
    )
  }

  commitSave = (displayText) => {
    if (!this.canSave()) return
    if (this.props.modalProps.saveCallback)
      this.props.modalProps.saveCallback(
        this.state.activeAsset,
        this.state.activeFolder,
        displayText,
        this.allowFolderTarget() && this.state.targetType === 'folder'
          ? 'folder'
          : 'asset',
      )
    else this.props.appActions.setActiveAsset(this.state.activeAsset)

    this.props.modalActions.closeModal()
  }

  save = () => {
    if (this.form) {
      this.form.validateFields((err, values) => {
        if (!err) this.commitSave(values.displayText)
      })
    } else {
      this.commitSave(null)
    }
  }

  cancel = () => {
    if (this.props.modalProps.cancelCallback)
      this.props.modalProps.cancelCallback()
    this.props.modalActions.closeModal()
  }

  nodeIsActive = (id, parentFolder = null) => {
    if (parentFolder) {
      return (
        this.state.activeNode === id && parentFolder === this.state.activeFolder
      )
    } else {
      return this.state.activeNode === id
    }
  }

  nodeIsOpen = (id) => this.state.openFolderKeys.indexOf(id) > -1

  nodeIsDraggable = () => {
    return false
  }

  nodeIsDisabled = (id, parentFolder) => {
    if (this.props.modalProps.carousel) {
      return this.props.modalProps.carousel.find(
        (featuredAsset) =>
          featuredAsset.asset === id && featuredAsset.folder === parentFolder,
      )
    }
    return false
  }

  onNodeClick = (id) => {
    let mutations = {
      activeNode: id,
      activeAsset: null,
    }
    if (this.state.activeFolder !== id) {
      mutations.activeFolder = id
    }
    // When folders are valid targets, clicking one selects it and seeds the
    // display text with its name, the way onAssetClick does with asset titles.
    // With a split tree, folders only count as targets in the folder tree.
    if (
      this.allowFolderTarget() &&
      (!this.hasSplitTree() || this.inFolderMode())
    ) {
      mutations.targetType = 'folder'
      if (this.props.folders.byId[id])
        mutations.textOverride = this.props.folders.byId[id].name
    }
    this.setState(mutations)
  }

  onAssetClick = (id, parentFolder) => {
    if (
      this.state.activeAsset !== id ||
      this.state.activeFolder !== parentFolder
    ) {
      this.setState({
        activeNode: id,
        activeAsset: id,
        activeFolder: parentFolder,
        targetType: 'asset',
        textOverride: this.props.assets.byId[id].title,
      })
    }
  }

  // Filter image files based on mime-type or extension
  imageAssetFilter = (id) => {
    if (!this.props.assets.byId[id]) return false
    return (
      this.props.assets.byId[id].file_type.startsWith('image') ||
      ['jpeg', 'jpg', 'gif', 'png', 'svg'].indexOf(
        this.props.assets.byId[id].extension,
      ) > -1
    )
  }

  // Filter PDF files based on mime-type or extension
  pdfAssetFilter = (id) => {
    if (!this.props.assets.byId[id]) return false
    return (
      this.props.assets.byId[id].file_type === 'application/pdf' ||
      this.props.assets.byId[id].extension === 'pdf'
    )
  }

  // Display only assets that are published or pending removal
  publishedAssetFilter = (id, parent) => {
    if (!this.props.assets.byId[id]) return false
    let filter = false
    if (
      this.props.endpoints.folderAssetStatus[parent] &&
      this.props.endpoints.folderAssetStatus[parent][id] &&
      this.props.endpoints.folderAssetStatus[parent][id].status === 'APPROVED'
    ) {
      filter = true
    }
    return filter
  }

  multiFilter = (filterArray) => {
    return (id, parent) => {
      let result = true
      filterArray.forEach((filter) => {
        result = result && filter(id, parent)
        return result
      })
      return result
    }
  }

  // Default asset filter (requires that asset have ID - which means it is not corrupted)
  defaultAssetFilter = (id) => this.props.assets.byId[id]

  toggleFolder = (id) => {
    if (this.state.openFolderKeys.indexOf(id) < 0) {
      this.setState({ openFolderKeys: this.state.openFolderKeys.concat([id]) })
    } else {
      this.setState({
        openFolderKeys: this.state.openFolderKeys.filter((key) => key !== id),
      })
    }
  }

  removeOpenFolderKey = (id) => {
    this.setState((prevState) => {
      return {
        openFolderKeys: prevState.openFolderKeys.filter((key) => key !== id),
      }
    })
  }

  nodeLink = () => this.props.location.pathname

  setActiveFolder = (id) => {
    this.setState({ activeFolder: id, activeAsset: null })
  }

  /*
   *  Returns a filter for the assets to be displayed.
   *  Accepts a filter function, an array or a string for predefined filters:
   *    'image'
   *    'pdf'
   *    'published'
   *  The array may contain a combination of predefined strings and functions
   */
  getFilter = (filterRequest) => {
    if (typeof filterRequest === 'function') return filterRequest

    const combinedFilters = []
    if (Array.isArray(filterRequest)) {
      filterRequest.forEach((request) => {
        combinedFilters.push(this.getFilter(request))
      })

      return this.multiFilter(combinedFilters)
    }

    switch (filterRequest) {
      case 'image':
        return this.imageAssetFilter
      case 'pdf':
        return this.pdfAssetFilter
      case 'published':
        return this.publishedAssetFilter
      default:
        return this.defaultAssetFilter
    }
  }

  // Not needed for Select Asset Modal
  allowPublishing = () => false

  // Not needed for select asset modal
  getBadgeCount = () => 0

  render() {
    return (
      <Modal
        title={this.props.modalProps.modalTitle || 'Assets'}
        okText="Select"
        onOk={this.save}
        cancelText="Cancel"
        onCancel={this.cancel}
        open={this.props.modal.modalOpen}
        confirmLoading={this.props.appState.asyncPending}
        maskClosable={false}
        style={{ top: 10 }}
        bodyStyle={{ paddingTop: 0, paddingBottom: 0 }}
      >
        {this.hasSplitTree() && (
          <div className="modal__text modal__flex">
            <span style={{ marginRight: 8 }}>Link to:</span>
            <Radio.Group
              size="small"
              optionType="button"
              buttonStyle="solid"
              value={this.state.treeMode}
              onChange={(e) => this.setTreeMode(e.target.value)}
              options={[
                { label: 'Asset', value: 'asset' },
                { label: 'Published Folder', value: 'folder' },
              ]}
            />
          </div>
        )}
        <div className="modal__text modal__flex">
          <span>
            {this.inFolderMode()
              ? 'Select a folder from the published content: '
              : !this.showAssets()
                ? 'Select a folder from the library: '
                : this.allowFolderTarget() && !this.hasSplitTree()
                  ? 'Select an asset or a folder from the library: '
                  : 'Select an asset from the library: '}
          </span>
        </div>
        <div className="modal-tree-container" ref={this.saveContainerRef}>
          {this.currentTreeRoot()
            .filter((folder) => {
              return (
                this.props.folders.byId[folder].type !== 'recycle' &&
                this.props.folders.byId[folder].type !== 'virtual' &&
                this.props.folders.byId[folder].type !== 'ugc'
              )
            })
            .map((folderid) => {
              return (
                <TreeNode
                  key={folderid}
                  {...this.props}
                  activeFolder={this.state.activeFolder}
                  openFolderKeys={this.state.openFolderKeys}
                  removeOpenFolderKey={this.removeOpenFolderKey}
                  node={this.props.folders.byId[folderid]}
                  nodeLink={this.nodeLink}
                  isFolder
                  nodeIsActive={this.nodeIsActive}
                  nodeIsOpen={this.nodeIsOpen}
                  isRoot={
                    this.props.folders.byId[folderid].parentId ? true : false
                  }
                  nodeIsDraggable={this.nodeIsDraggable}
                  rightClickDisabled={true}
                  onToggle={this.toggleFolder}
                  onNodeClick={this.onNodeClick}
                  nodeIsDisabled={this.nodeIsDisabled}
                  setActiveFolder={this.setActiveFolder}
                  menuItem={this.props.appState.menuItem}
                  showAssets={this.showAssets()}
                  // Assets always list by title, falling back to the filename
                  displayMode="title"
                  allAssets={this.props.assets.byId}
                  onAssetClick={this.onAssetClick}
                  assetFilter={this.getFilter(this.props.modalProps.filterType)}
                  allowPublishing={this.allowPublishing}
                  badges={[]}
                  getBadgeCount={this.getBadgeCount}
                  style={{
                    treenodeText: {
                      color: 'rgba(0,0,0,.65)',
                    },
                    treenodeIcon: {
                      color: 'rgba(0,0,0,.65)',
                    },
                  }}
                />
              )
            })}
        </div>
        {this.showAssets() && this.state.activeAsset && (
          <div className="builders__asset_preview_container modal__label-padded-top">
            <AssetPreviewThumbnail
              asset={this.props.assets.byId[this.state.activeAsset]}
              width="100%"
              height="100%"
              imageStyles={{ objectFit: 'contain' }}
            />
          </div>
        )}
        {this.props.modalProps.showTextOverride && (
          <DisplayTextOverride
            ref={this.saveFormRef}
            textOverrideDescription={
              this.props.modalProps.textOverrideDescription
            }
            value={this.state.textOverride}
            onChange={(e) => this.setState({ textOverride: e.target.value })}
            maxLength={this.props.modalProps.maxDisplayText}
          />
        )}
      </Modal>
    )
  }
}

/*
 *  modal/modalActions and folder/folderActions are the redux state and actions
 */
SelectAssetModal.propTypes = {
  modal: PropTypes.object,
  modalProps: PropTypes.object,
  modalActions: PropTypes.object,
  appState: PropTypes.object,
  folders: PropTypes.object,
  folderActions: PropTypes.object,
  user: PropTypes.object,
}

export default SelectAssetModal
